import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border bg-background p-4 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'border-border text-foreground',
        destructive: 'border-destructive/40 text-destructive [&>svg]:text-current',
        warning: 'border-warning text-warning [&>svg]:text-current',
        info: 'border-none bg-info-soft',
        success: 'border-none bg-success-soft',
        urgent:
          'border-none bg-destructive text-primary-foreground [&_[data-slot=alert-description]]:text-inherit [&_[data-slot=alert-title]]:text-inherit [&_a]:text-inherit [&_a]:underline',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 text-base leading-none font-medium tracking-tight text-foreground',
        className
      )}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-sm leading-normal text-foreground',
        className
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
